/**
 * 多次散射 LUT 片段着色器（Hillaire 2020 风格，2D 简化版）。
 *
 * 2D 查找表 texel：(x = V·L, y = L·up)，存储地面处从视线方向 V 到达的
 * 多次散射辐亮度（含太阳辐照度），其中 L 为太阳方向、up 为本地天顶。
 *
 * 计算：M(cosTheta, cosThetaSun) = ∫_Ω S1(ω)·P̄(ω→V) dω
 *  - S1(ω)：地面处来自方向 ω 的单散射辐亮度（共享 computeSingleScatter）
 *  - P̄：Rayleigh+Mie 加权混合相位（第二次散射的相位）
 *  - 球面积分用 Fibonacci 黄金螺旋均匀采样（32 个方向）
 *
 * 主着色器按 (V·L, L·up) 采样该 LUT，作为多次散射填充项。
 */

import { atmosphereCommonChunk } from './common.glsl'

export const multiscatterLUTFragmentShader = /* glsl */ `
precision highp float;

uniform vec2 uResolution;      // LUT 尺寸
uniform sampler2D uTransLUT;
uniform vec3 uBetaR;
uniform vec3 uBetaM;
uniform vec3 uBetaMExt;
uniform float uHr;
uniform float uHm;
uniform float uPlanetR;
uniform float uAtmosR;
uniform float uG;
uniform float uSunIrradiance;
uniform int uSampleSteps;      // 内层 ray march 步数（固定，保证 LUT 平滑）
uniform int uRayleighOnly;
uniform int uMieOnly;
uniform int uNoScatter;

varying vec2 vUv;

${atmosphereCommonChunk}

// Fibonacci 黄金螺旋球面均匀方向（i ∈ [0, N)）
vec3 sphereDir(int i, int N) {
  float phi = acos(1.0 - 2.0 * (float(i) + 0.5) / float(N));
  float theta = 2.39996322972865332 * float(i);
  float sp = sin(phi);
  return vec3(sp * cos(theta), sp * sin(theta), cos(phi));
}

void main() {
  float cosTheta = vUv.x * 2.0 - 1.0;      // V·L ∈ [-1,1]
  float cosThetaSun = vUv.y * 2.0 - 1.0;   // L·up ∈ [-1,1]
  float sinTheta = sqrt(max(1.0 - cosTheta * cosTheta, 0.0));
  float sinThetaSun = sqrt(max(1.0 - cosThetaSun * cosThetaSun, 0.0));

  // 太阳方向（up = +Z），固定方位角 0
  vec3 sunDir = vec3(sinThetaSun, 0.0, cosThetaSun);
  // 与 L 垂直、位于 sun/up 平面内的单位向量（视线上方的"竖直"分量）
  vec3 T;
  if (abs(cosThetaSun) > 0.999) {
    T = vec3(1.0, 0.0, 0.0); // L ∥ up 时退化为任意垂直方向
  } else {
    T = normalize(vec3(-cosThetaSun, 0.0, sinThetaSun));
  }
  // 视线方向 V：与 L 夹角余弦 = cosTheta，且尽量贴近 up
  vec3 viewDir = normalize(sunDir * cosTheta + T * sinTheta);
  vec3 groundPos = vec3(0.0, 0.0, uPlanetR);

  // 球面积分：M = ∫ S1(ω)·P̄(ω→V) dω ≈ (4π/N) Σ S1(ωᵢ)·P̄(ωᵢ·V)
  vec3 acc = vec3(0.0);
  for (int i = 0; i < 32; i++) {
    vec3 wi = sphereDir(i, 32);
    vec3 tauDummy;
    vec3 s1 = computeSingleScatter(groundPos, wi, sunDir, tauDummy);
    acc += s1 * totalPhase(dot(wi, viewDir));
  }
  vec3 M = acc * (4.0 * PI / 32.0);

  gl_FragColor = vec4(M, 1.0);
}
`
